import React, { useContext, useMemo, useState } from "react";
import { FaUserPlus } from "react-icons/fa";
import { UserContext } from "../../store/context/UserContext";
import InviteAdditionalMemberDrawer from "./InviteAdditionalMemberDrawer";
import {
  fetchLatestEddRequest,
  removeEddSpecialEmail,
  saveEddSpecialEmail,
} from "./inviteMemberApi";
import {
  canInviteAdditionalMemberForEdd,
  canInviteAdditionalMember,
  resolveApplicationFormId,
} from "./inviteMemberUtils";
import "./styles.scss";

const ApplicantHeaderActions = ({
  applicationForm,
  formData,
  formId,
  formType,
  onApplicationFormUpdated,
  children,
  logoutComponent,
  disabled,
  invitePersistence = "application-form",
}) => {
  const { userContent } = useContext(UserContext) || {};
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const resolvedFormId = resolveApplicationFormId({ formId, applicationForm, formData });
  const currentUserEmail = userContent?.email;
  const isEddInvitePersistence = invitePersistence === "edd-request";

  const actions = useMemo(() => {
    const availableActions = [];

    const canShowInviteAction = isEddInvitePersistence
      ? canInviteAdditionalMemberForEdd({
        currentUserEmail,
        applicationForm,
        formData,
        formId: resolvedFormId,
        disabled,
      })
      : canInviteAdditionalMember({
        currentUserEmail,
        applicationForm,
        formData,
        formId: resolvedFormId,
        disabled,
      });

    if (canShowInviteAction) {
      availableActions.push({
        key: "invite-member",
        label: "Invite additional member",
        // Shown instead of `label` on narrow viewports so the header never overflows.
        shortLabel: "Invite",
        icon: <FaUserPlus aria-hidden="true" />,
        onClick: () => setIsInviteDrawerOpen(true),
      });
    }

    return availableActions;
  }, [
    applicationForm,
    currentUserEmail,
    disabled,
    formData,
    isEddInvitePersistence,
    resolvedFormId,
  ]);

  const eddInviteApis = useMemo(() => {
    if (!isEddInvitePersistence || !resolvedFormId) {
      return {};
    }

    const normalizeEddInviteResult = (updatedEddRequest = {}) => ({
      ...(applicationForm || {}),
      specialEmails: updatedEddRequest?.specialEmails || [],
    });

    return {
      fetchInviteRecordApi: async () => {
        const latestInviteRecord = await fetchLatestEddRequest({ applicationId: resolvedFormId });
        return normalizeEddInviteResult(latestInviteRecord);
      },
      saveSpecialEmailApi: async ({ name, email }) => normalizeEddInviteResult(
        await saveEddSpecialEmail({ applicationId: resolvedFormId, name, email })
      ),
      removeSpecialEmailApi: async () => normalizeEddInviteResult(
        await removeEddSpecialEmail({ applicationId: resolvedFormId })
      ),
    };
  }, [applicationForm, isEddInvitePersistence, resolvedFormId]);

  const hasActions = actions.length > 0;

  if (!hasActions) {
    return (
      <>
        {children}
        {logoutComponent}
      </>
    );
  }

  return (
    <div
      className="applicant-header-actions"
      data-testid="applicant-header-actions"
      data-form-type={formType || undefined}
    >
      {children}

      {/* Rendered inline (previously hidden behind an ellipsis menu) and placed before Logout so it
          sits alongside the other primary header actions - applicants were not discovering it. */}
      {actions.map((action) => (
        <button
          key={action.key}
          type="button"
          // Reuse the shared header button geometry (same height/radius/typography as Logout) and
          // take the outline variant so it reads as secondary next to the solid black Logout.
          className="form-content-btn-outline applicant-header-actions__action"
          onClick={() => action.onClick?.()}
          // The button shows the short label to stay compact in the header; the full wording is kept
          // as the tooltip and the accessible name.
          title={action.label}
          aria-label={action.label}
          data-testid={`applicant-header-actions-item-${action.key}`}
        >
          {action.icon}
          <span className="applicant-header-actions__action-label">
            {action.shortLabel || action.label}
          </span>
        </button>
      ))}

      {logoutComponent}

      <InviteAdditionalMemberDrawer
        isOpen={isInviteDrawerOpen}
        onClose={() => setIsInviteDrawerOpen(false)}
        formId={resolvedFormId}
        applicationForm={applicationForm}
        currentUserEmail={currentUserEmail}
        onApplicationFormUpdated={onApplicationFormUpdated}
        {...eddInviteApis}
      />
    </div>
  );
};

export default ApplicantHeaderActions;
